import { useMountEffect } from "@react-hookz/web";
import React, { useCallback, useEffect, useRef } from "react";
import { CloseButton } from "scmp-sdk/components/acquisition-popup-container/display/popups/common";
import { PopupBackdrop } from "scmp-sdk/components/acquisition-popup-container/display/styles";
import type { PopupProps } from "scmp-sdk/components/acquisition-popup-container/types";
import { useSdkContext } from "scmp-sdk/components/contexts";
import {
  FollowAction,
  FollowButtonOutline,
  FollowButtonSize,
  FollowButtonVariant,
} from "scmp-sdk/components/follow-button/enums";
import { isFollowType } from "scmp-sdk/components/follow-button/helpers";
import { Modal } from "scmp-sdk/components/modal";

import { getCTAType, useActionButtonRenderer } from "./helpers";
import { Container, ContentContainer, Description, Image, TagLine, Title } from "./styles";
import type { Campaign } from "./types";

export type Props = PopupProps<{
  campaign: Campaign;
  confirmedAction?: boolean;
}>;

export const CampaignPopup: React.FunctionComponent<Props> = ({
  campaign,
  confirmedAction,
  modalContainerReference,
  onClose,
}) => {
  const { handleFollow, handleTrackGA4 } = useSdkContext();
  const { content, layout = "default", tagline, theme } = campaign;
  const { description, image, title } = content;
  const actionButtonRenderer = useActionButtonRenderer(campaign);

  const handleFollowReference = useRef(handleFollow);
  const handleTrackGA4Reference = useRef(handleTrackGA4);

  const campaignName = campaign.campaign;
  const ctaType = getCTAType(theme);

  useMountEffect(() => {
    if (confirmedAction) return;

    handleTrackGA4({
      action: "imp",
      category: "promopopup",
      customized_parameters: {
        cta_type: ctaType,
        internal_campaign: campaignName,
        step: "1",
      },
      subcategory: "",
    });
  });

  const handleClose = useCallback(() => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: ctaType,
        internal_campaign: campaignName,
        message: "close button",
        step: "1",
      },
      subcategory: "",
    });
    onClose();
  }, [campaignName, ctaType, onClose, handleTrackGA4]);

  useEffect(() => {
    const { tid, tids = [], type } = campaign;

    if (!confirmedAction) return;
    if (!handleFollowReference.current) return;
    if (!handleTrackGA4Reference.current) return;
    if (!isFollowType(type)) return;

    handleFollowReference.current({
      action: FollowAction.Follow,
      callback: () => {
        handleTrackGA4Reference.current({
          action: "sys",
          category: "follow",
          customized_parameters: {
            action_type: "follow",
            follow_type: "topic",
            internal_campaign: campaignName,
            topic_id: tid ?? tids.join(", "),
            trigger_point: "ppto",
          },
          subcategory: "result",
        });
      },
      entityIds: tid ? [tid] : tids,
      followType: type,
    });

    onClose();
  }, [
    campaign,
    campaignName,
    confirmedAction,
    handleFollowReference,
    handleTrackGA4Reference,
    onClose,
  ]);

  if (confirmedAction) return null;
  return (
    <Modal
      BackdropComponent={PopupBackdrop}
      container={modalContainerReference?.current}
      fadeTimeout={400}
      open={true}
    >
      <Container $variant={layout}>
        <CloseButton onClose={handleClose} />
        <Image alt={title} src={image} />
        <ContentContainer>
          <TagLine
            dangerouslySetInnerHTML={{
              __html: tagline,
            }}
          />
          <Title
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          <Description
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
          {actionButtonRenderer.render({
            followButtonProps: {
              outline:
                layout === "soft" ? FollowButtonOutline.Filled : FollowButtonOutline.Outlined,
              size: layout === "soft" ? FollowButtonSize.Large : FollowButtonSize.Normal,
              variant: layout === "soft" ? FollowButtonVariant.Light : FollowButtonVariant.Dark,
            },
            handlePopupClose: onClose,
          })}
        </ContentContainer>
      </Container>
    </Modal>
  );
};

CampaignPopup.displayName = "CampaignPopup";
